import { useTranslation as useI18nTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';

/**
 * Hook customizado para traduções
 * Fornece uma interface simplificada para usar traduções na aplicação
 * 
 * @param {string} namespace - O namespace das traduções (opcional, padrão: 'common')
 * @returns {object} Objeto com função de tradução e utilitários
 */
export const useTranslation = (namespace = 'common') => {
  const { t, i18n } = useI18nTranslation(namespace);
  const { formatDate, formatNumber, currentLanguage } = useLanguage();

  /**
   * Função de tradução com suporte a múltiplos namespaces
   * @param {string} key - Chave da tradução (pode incluir namespace com :)
   * @param {object} options - Opções de interpolação
   * @returns {string} Texto traduzido
   */
  const translate = (key, options = {}) => {
    // Se a chave contém namespace, usar diretamente
    if (key.includes(':')) {
      return t(key, options);
    }
    
    // Tentar no namespace atual primeiro
    const currentNsKey = `${namespace}:${key}`;
    if (i18n.exists(currentNsKey)) {
      return t(key, options);
    }
    
    // Fallback para namespace comum
    const commonKey = `common:${key}`;
    if (i18n.exists(commonKey)) {
      return t(commonKey, options);
    }
    
    // Fallback final
    return t(key, options);
  };

  /**
   * Função para traduzir com namespace específico
   * @param {string} ns - Namespace
   * @param {string} key - Chave da tradução
   * @param {object} options - Opções de interpolação
   * @returns {string} Texto traduzido
   */
  const translateWithNamespace = (ns, key, options = {}) => {
    return t(`${ns}:${key}`, options);
  };

  /**
   * Função para traduzir status de jobs
   * @param {string} status - Status do job
   * @returns {string} Status traduzido
   */
  const translateJobStatus = (status) => {
    return translate(`jobs:status.${status}`) || status;
  };

  /**
   * Função para traduzir datas relativas (ex: "há 2 dias")
   * @param {Date|string} date - Data para comparar
   * @returns {string} Data relativa traduzida
   */
  const translateRelativeDate = (date) => {
    const now = new Date();
    const targetDate = date instanceof Date ? date : new Date(date);
    const diffInMinutes = Math.floor((now - targetDate) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return translate('common:justNow');
    } else if (diffInMinutes < 60) {
      return translate('common:minutesAgo', { count: diffInMinutes });
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return translate('common:hoursAgo', { count: hours });
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return translate('common:daysAgo', { count: days });
    }
  };

  return {
    t: translate,
    translate,
    translateWithNamespace,
    translateJobStatus,
    translateRelativeDate,
    formatDate,
    formatNumber,
    currentLanguage,
    changeLanguage: i18n.changeLanguage,
  };
};

export default useTranslation; 